'use client';

import React from 'react';
import { styled } from '@linaria/react';

import useScrollPosition from '@/hooks/use-scroll-position';
import useWindowDimensions from '@/hooks/use-window-dimensions';
import { useContent } from '@/components/ContentProvider';

import ConnectedLikeButton from '@/components/LikeButton/ConnectedLikeButton';
import ConnectedLikeCount from '@/components/LikeButton/ConnectedLikeCount';

const MINIMUM_BUFFER = 300;

interface Props {
  maxArticleWidth: number;
}

const FloatingSidebar = ({ maxArticleWidth }: Props) => {
  const { metrics } = useContent();
  const [, scrollY] = useScrollPosition();
  const { width: windowWidth } = useWindowDimensions();

  // The tricky thing here is that different articles have different maximum widths. I used to dynamically generate the media query using the `maxArticleWidth` prop, but that doesn’t work with Linaria / static CSS-in-JS.
  // So here’s what I need to do instead: we’ll track the window width in state, and derive whether it’s visible or not based on that value.
  const maxArticleWidthInPixels = maxArticleWidth * 16;
  const isVisible =
    typeof windowWidth === 'number' &&
    typeof scrollY === 'number' &&
    scrollY >= 250 &&
    windowWidth > maxArticleWidthInPixels + MINIMUM_BUFFER;

  const shiftBy = maxArticleWidth / 2 + 4;

  const hasLoaded = typeof metrics.totalNumLikes === 'number';

  return (
    <Wrapper
      style={{
        '--shift-by': `${shiftBy}rem`,
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        transition: isVisible
          ? `opacity 500ms, visibility 0ms`
          : `opacity 500ms, visibility 0ms 500ms`,
      }}
    >
      {hasLoaded && <Background />}
      <ConnectedLikeButton />
      <ConnectedLikeCount />
    </Wrapper>
  );
};

const Background = styled.div`
  position: absolute;
  inset: -16px;
  background: var(--color-background);
  background: hsl(from var(--color-background) h s l / 0.75);
  backdrop-filter: blur(4px);
  border-radius: 16px;
`;

const Wrapper = styled.div`
  position: fixed;
  top: 16rem;
  left: calc(50vw + var(--shift-by));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export default FloatingSidebar;
